import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User, Mail, Lock, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';
import { auth } from '../../../firebase';
import onboardingService from '../../../services/onboardingService';
import authService from '../../../services/authService';
import { useAuth } from '../../../contexts/AuthContext';

const stripAdminMeta = (raw = {}) => {
  const {
    isPartial,
    lastSaved,
    lastSavedAt,
    readyForSubmission,
    onboardingProgress,
    createdAt,
    updatedAt,
    metadata,
    ...rest
  } = raw;
  return rest;
};

const AdminUserStep = ({ data, updateData, registerNextHandler }) => {
  const normalizedAdminData = useMemo(() => stripAdminMeta(data?.adminUser || {}), [data?.adminUser]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    ...normalizedAdminData
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  const { currentUser, register } = useAuth();
  const isExistingAdmin = Boolean(currentUser || data?.adminUser?.uid);

  // If user is already logged in, use their information
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        email: currentUser.email || '',
        firstName: currentUser.displayName?.split(' ')[0] || '',
        lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || ''
      }));
    }
  }, [currentUser]);

  useEffect(() => {
    if (data?.adminUser) {
      setFormData(prev => ({
        ...prev,
        ...stripAdminMeta(data.adminUser),
        password: '',
        confirmPassword: ''
      }));
    }
  }, [data?.adminUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific field error
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Format phone number as user types
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    } else if (value.length >= 3) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    }
    
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Only validate password if user is not already logged in
    if (!currentUser) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (formData.phone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be in format (555) 123-4567';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const performAdminSetup = useCallback(async () => {
    if (!validateForm()) {
      return false;
    }

    setErrors(prev => ({ ...prev, submit: '' }));
    setIsCreating(true);

    try {
      let userId = currentUser?.uid;

      if (!userId) {
        const registrationPayload = {
          email: formData.email.trim(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: formData.phone.trim(),
          role: 'admin',
          companyId: ''
        };

        console.log('📡 ADMIN-STEP: Registering new admin user via backend API', {
          email: registrationPayload.email,
          firstName: registrationPayload.firstName,
          lastName: registrationPayload.lastName,
          phone: registrationPayload.phone,
          role: registrationPayload.role
        });

        const registrationResult = await register(registrationPayload);

        if (!registrationResult?.success) {
          throw new Error(registrationResult?.error || 'Failed to create admin account');
        }

        userId = auth.currentUser?.uid;
        console.log('✅ ADMIN-STEP: Firebase user created successfully', { userId });

        if (!userId) {
          await auth.currentUser?.reload();
          userId = auth.currentUser?.uid;
        }

        if (!userId) {
          throw new Error('Unable to determine admin user ID after registration');
        }
      } else {
        console.log('ℹ️ ADMIN-STEP: Existing authenticated user detected', { userId });
        // Creates the users doc if it is missing; an existing doc keeps its role and company.
        await authService.completeProfile({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: formData.phone.trim(),
          companyId: ''
        });
      }

      const adminProfile = {
        uid: userId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        role: 'admin',
        permissions: {
          canViewAllDrivers: true,
          canEditDocuments: true,
          canDeleteDocuments: true,
          canManageUsers: true,
          canViewReports: true
        }
      };

      console.log('💾 ADMIN-STEP: Saving admin profile to Firestore onboarding document', adminProfile);
      await onboardingService.saveUserDataIncremental(userId, adminProfile, false);
      console.log('✅ ADMIN-STEP: Admin profile persisted to Firestore');

      // The users doc itself is written server-side by /users/complete-profile
      // (Firestore rules deny browser writes to role, permissions and companyId).

      await updateData('adminUser', adminProfile);
      console.log('✅ ADMIN-STEP: Onboarding wizard data updated with admin profile');

      console.log('✅ ADMIN-STEP: Admin setup completed successfully');
      return true;
    } catch (error) {
      console.error('❌ ADMIN-STEP: Failed to create admin account or persist profile', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to create admin account'
      }));
      return false;
    } finally {
      setIsCreating(false);
    }
  }, [currentUser, formData, register]);

  useEffect(() => {
    if (!registerNextHandler) {
      return undefined;
    }

    const unregister = registerNextHandler(performAdminSetup);
    return () => {
      unregister?.();
    };
  }, [registerNextHandler, performAdminSetup]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
          <Shield className="h-6 w-6 text-blue-600" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          {currentUser ? 'Verify Admin Information' : 'Create Administrator Account'}
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          {currentUser 
            ? 'Please verify your information and complete your profile.'
            : 'This will be the primary administrator account for your company.'
          }
        </p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        {errors.submit && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            </div>
          </div>
        )}

        {/* Personal Information */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2 text-blue-600" />
            Personal Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                required
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={isCreating}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter first name"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                required
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={isCreating}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter last name"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address *
            </label>
            <div className="mt-1 relative">
              <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                disabled={isExistingAdmin || isCreating}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isExistingAdmin || isCreating ? 'bg-gray-50 text-gray-500' : ''
                } ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="admin@company.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
            {isExistingAdmin && (
              <p className="mt-1 text-sm text-gray-500">
                Email address is locked after account creation
              </p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              disabled={isCreating}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.phone ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="(555) 123-4567"
              maxLength="14"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>
        </div>

        {/* Password Section - Only show if user is not logged in */}
        {!currentUser && (
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900 flex items-center">
              <Lock className="h-5 w-5 mr-2 text-blue-600" />
              Account Security
            </h4>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  required
                  value={isExistingAdmin ? '********' : formData.password}
                  onChange={handleInputChange}
                  disabled={isCreating || isExistingAdmin}
                  className={`block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } ${isExistingAdmin ? 'bg-gray-50 text-gray-500' : ''}`}
                  placeholder={isExistingAdmin ? 'Password set' : 'Create a secure password'}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isCreating || isExistingAdmin}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                ) : (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password *
              </label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  required
                  value={isExistingAdmin ? '********' : formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={isCreating || isExistingAdmin}
                  className={`block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } ${isExistingAdmin ? 'bg-gray-50 text-gray-500' : ''}`}
                  placeholder={isExistingAdmin ? 'Password set' : 'Confirm your password'}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isCreating || isExistingAdmin}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                ) : (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
        )}

        {/* Admin Privileges Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Shield className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Administrator Privileges
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>As an administrator, you will have full access to:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Manage all drivers and vehicles</li>
                  <li>View and edit all documents</li>
                  <li>Configure system settings</li>
                  <li>Manage user accounts and permissions</li>
                  <li>Generate compliance reports</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {isCreating && (
          <div className="flex items-center pt-2 text-sm text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Saving admin account...
          </div>
        )}
      </form>
    </div>
  );
};

export default AdminUserStep;
